import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { CalendarOff, Sparkles, Briefcase, Clock4 } from "lucide-react";
import { logger } from "@/lib/logger";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const fmtNum = (n) => (Number(n) || 0).toFixed(1).replace(/\.0$/, "");

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function Card({ icon: Icon, label, accrued, used, remaining, carry, accent, sub }) {
  const palette = {
    emerald: "from-emerald-50 border-emerald-200 text-emerald-900",
    amber: "from-amber-50 border-amber-200 text-amber-900",
    blue: "from-blue-50 border-blue-200 text-blue-900",
    indigo: "from-indigo-50 border-indigo-200 text-indigo-900",
  };
  const cls = palette[accent] || "from-zinc-50 border-zinc-200";
  return (
    <div className={`bg-gradient-to-br ${cls} to-white border rounded-md p-5`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="label-overline">{label}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-4xl font-bold font-display font-mono">{fmtNum(remaining)}</span>
        <span className="text-xs text-zinc-500">days available</span>
      </div>
      <div className="mt-3 text-xs text-zinc-600 grid grid-cols-2 gap-y-0.5">
        <span>Accrued</span><span className="font-mono text-right">{fmtNum(accrued)}</span>
        {carry !== undefined && (<><span>Carry forward</span><span className="font-mono text-right">{fmtNum(carry)}</span></>)}
        <span>Used</span><span className="font-mono text-right">{fmtNum(used)}</span>
      </div>
      {sub && <div className="mt-2 text-[11px] text-zinc-500 italic">{sub}</div>}
    </div>
  );
}

export default function MyLeaveBalance() {
  const [bal, setBal] = useState(null);
  const [ot, setOt] = useState(null);
  const [period, setPeriod] = useState(currentMonth());

  useEffect(() => {
    (async () => {
      try {
        const [b, o] = await Promise.all([api.get("/leave-balances/me"), api.get(`/overtime/me?period_month=${period}`)]);
        setBal(b.data);
        setOt(o.data);
      } catch (err) {
        logger.error("Failed to load leave balance / overtime:", err);
      }
    })();
  }, [period]);

  if (!bal) return <div className="text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Compensation · Time off</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">My Leave Balance</h1>
        <p className="text-sm text-zinc-500 mt-2">Financial year <span className="font-mono">{bal.fy}</span>. Accrual: 1.5 EL · 1 SL · 1 CL per completed month.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Card icon={Sparkles} label="Earned Leave (EL)" accrued={bal.el_accrued} used={bal.used?.el} remaining={bal.el_remaining} carry={bal.el_carry_forward} accent="emerald" sub="50% carries forward at year-end; rest is encashed." />
        <Card icon={CalendarOff} label="Sick Leave (SL)" accrued={bal.sl_accrued} used={bal.used?.sl} remaining={bal.sl_remaining} accent="amber" sub="Unused SL is encashed at year-end." />
        <Card icon={Briefcase} label="Casual Leave (CL)" accrued={bal.cl_accrued} used={bal.used?.cl} remaining={bal.cl_remaining} accent="blue" sub="Unused CL is encashed at year-end." />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock4 className="w-4 h-4 text-zinc-700" />
            <span className="label-overline">Overtime · {period}</span>
          </div>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-zinc-200 rounded px-2 py-1 text-xs" data-testid="ot-month" />
        </div>
        {ot ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Required</div><div className="font-mono text-lg">{ot.required_hours}h</div><div className="text-[10px] text-zinc-500">{ot.working_days} × 12h</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Clocked</div><div className="font-mono text-lg">{ot.clocked_hours}h</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">OT hours</div><div className="font-mono text-lg text-emerald-700">{ot.ot_hours}h</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">OT amount</div><div className="font-mono text-lg font-bold text-emerald-700">{fmtINR(ot.ot_amount)}</div><div className="text-[10px] text-zinc-500">@ ₹{ot.ot_rate}/hr</div></div>
          </div>
        ) : <div className="text-zinc-500 text-sm">Loading…</div>}
      </div>

      {bal.finalized && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-5">
          <div className="label-overline mb-2">Year-end encashment (FY {bal.fy})</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Daily rate (March ÷ 30)</div><div className="font-mono">{fmtINR(bal.march_daily_rate)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">EL carried to next year</div><div className="font-mono">{fmtNum(bal.el_carried_to_next)} days</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">EL encashed</div><div className="font-mono">{fmtNum(bal.el_encashed_days)} × {fmtINR(bal.march_daily_rate)} = {fmtINR(bal.el_encashed_amount)}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">SL + CL encashed</div><div className="font-mono">{fmtINR((bal.sl_encashed_amount || 0) + (bal.cl_encashed_amount || 0))}</div></div>
          </div>
          <div className="mt-3 text-lg font-bold font-mono text-emerald-700">Total payout: {fmtINR(bal.total_encashed_amount)}</div>
        </div>
      )}
    </div>
  );
}
